// Dose arithmetic for prescribing (docs/plans/phase1-consultation.md §5.2).
//
// Doses, strengths and weights are clinical numbers, so nothing here uses floating point.
// A decimal typed by a prescriber ("2.5") becomes an exact rational number of two
// `bigint`s; multiplication, division and comparison stay exact, and rounding happens once,
// at display or when a whole number of base units is needed.
//
// Units convert only inside mass (g, mg, ug) and inside volume (L, mL), where the factors
// are definitions, not clinical knowledge. Every other pair is `not_convertible`: Wodi does
// not invent factors (mg to mL depends on the product's strength, which the formulary
// states per ingredient, and IU or mmol to mass depends on the substance).
//
// Nothing here holds a dose limit. Limits are tenant data (rules.ts, checks.ts).

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

// --- Exact rationals --------------------------------------------------------------------------

/** An exact non-negative rational number. Always normalised: den > 0, gcd(num, den) = 1. */
export interface Ratio {
  readonly num: bigint;
  readonly den: bigint;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
}

export function ratio(num: bigint, den = 1n): Ratio {
  if (den === 0n) throw new RangeError('A ratio cannot have a zero denominator');
  const sign = den < 0n ? -1n : 1n;
  const g = gcd(num, den) || 1n;
  return { num: (sign * num) / g, den: (sign * den) / g };
}

export const ZERO: Ratio = { num: 0n, den: 1n };
export const ONE: Ratio = { num: 1n, den: 1n };

export function ratioFromInteger(value: number | bigint): Ratio {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) {
    throw new RangeError('Expected a whole number');
  }
  return ratio(BigInt(value));
}

/** A scaled integer (value × 10^decimals) as an exact ratio. */
export function ratioFromScaled(scaled: number | bigint, decimals: number): Ratio {
  if (typeof scaled === 'number' && !Number.isSafeInteger(scaled)) {
    throw new RangeError('Expected a whole scaled number');
  }
  return ratio(BigInt(scaled), 10n ** BigInt(decimals));
}

export function multiply(a: Ratio, b: Ratio): Ratio {
  return ratio(a.num * b.num, a.den * b.den);
}

export function divide(a: Ratio, b: Ratio): Ratio {
  if (b.num === 0n) throw new RangeError('Division by zero');
  return ratio(a.num * b.den, a.den * b.num);
}

export function add(a: Ratio, b: Ratio): Ratio {
  return ratio(a.num * b.den + b.num * a.den, a.den * b.den);
}

export function subtract(a: Ratio, b: Ratio): Ratio {
  return ratio(a.num * b.den - b.num * a.den, a.den * b.den);
}

/** -1, 0 or 1. */
export function compare(a: Ratio, b: Ratio): -1 | 0 | 1 {
  const left = a.num * b.den;
  const right = b.num * a.den;
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

/** The smallest whole number not below the value. */
export function ceilRatio(r: Ratio): bigint {
  const q = r.num / r.den;
  return r.num % r.den === 0n || r.num < 0n ? q : q + 1n;
}

/** The largest whole number not above the value (for non-negative values). */
export function floorRatio(r: Ratio): bigint {
  if (r.num < 0n) throw new RangeError('floorRatio expects a non-negative value');
  return r.num / r.den;
}

/** value × 10^decimals, rounded half up. For non-negative values. */
export function roundToScaled(r: Ratio, decimals: number): bigint {
  const scale = 10n ** BigInt(decimals);
  const scaled = r.num * scale * 2n + r.den;
  return scaled / (2n * r.den);
}

/**
 * A decimal string for display and for the API ("2.5", "1500", "0.125"): rounded half up
 * to `decimals` places, trailing zeros removed.
 */
export function formatRatio(r: Ratio, decimals = 3): string {
  const scaled = roundToScaled(r, decimals);
  const scale = 10n ** BigInt(decimals);
  const whole = scaled / scale;
  const fraction = (scaled % scale).toString().padStart(decimals, '0').replace(/0+$/, '');
  return fraction === '' ? whole.toString() : `${whole.toString()}.${fraction}`;
}

// --- Parsing ----------------------------------------------------------------------------------

export const DOSE_DECIMALS = 3;

/** Digits before the decimal point: far beyond any dose, and it stops absurd input. */
const MAX_INTEGER_DIGITS = 9;

export type DecimalErrorCode =
  | 'dose.empty'
  | 'dose.not_a_number'
  | 'dose.too_many_decimals'
  | 'dose.not_positive'
  | 'dose.too_large';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const DOSE_ERRORS: Record<DecimalErrorCode, string> = {
  'dose.empty': 'Enter the dose',
  'dose.not_a_number': 'Enter numbers only; choose the unit separately',
  'dose.too_many_decimals': 'Too many decimal places',
  'dose.not_positive': 'The dose must be more than zero',
  'dose.too_large': 'That number is too large',
};

/**
 * Reads a dose as typed: `2`, `2.5`, `2,5` (both separators are typed on Kenyan keyboards),
 * `.5`. Units, signs, grouping and extra decimals are refused, never guessed at. The result
 * is the scaled integer (value × 10^decimals).
 */
export function parseDose(
  text: string,
  decimals = DOSE_DECIMALS,
): Result<bigint, DecimalErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('dose.empty');
  if (trimmed.startsWith('-')) return err('dose.not_positive');
  const match = /^(\d*)(?:[.,](\d*))?$/.exec(trimmed);
  if (match === null) return err('dose.not_a_number');
  const integerPart = (match[1] ?? '').replace(/^0+(?=\d)/, '');
  const fraction = match[2] ?? '';
  if (integerPart === '' && fraction === '') return err('dose.not_a_number');
  if (fraction.length > decimals) return err('dose.too_many_decimals');
  if (integerPart.length > MAX_INTEGER_DIGITS) return err('dose.too_large');
  const scaled = BigInt((integerPart === '' ? '0' : integerPart) + fraction.padEnd(decimals, '0'));
  if (scaled === 0n) return err('dose.not_positive');
  return ok(scaled);
}

/** A dose or strength as an exact ratio, from typed text or a stored decimal string. */
export function parseDecimal(
  text: string,
  decimals = DOSE_DECIMALS,
): Result<Ratio, DecimalErrorCode> {
  // The database pads numeric values ("250.000"); allow up to its scale of zeros.
  const trimmed = text
    .trim()
    .replace(/^(\d+\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
  const parsed = parseDose(trimmed, decimals);
  return parsed.ok ? ok(ratioFromScaled(parsed.value, decimals)) : parsed;
}

// --- Units ------------------------------------------------------------------------------------

/** Exact factors within one dimension, to the smallest unit of that dimension. */
const MASS_IN_UG: Record<string, bigint> = { g: 1_000_000n, mg: 1_000n, ug: 1n };
const VOLUME_IN_ML: Record<string, Ratio> = { L: ratio(1000n), mL: ONE };

/** UCUM units a dose rule or strength can be written in (plan §2.9). */
export const MEASURABLE_UNITS = ['g', 'mg', 'ug', 'L', 'mL', '[iU]', 'mmol'] as const;
export type MeasurableUnit = (typeof MEASURABLE_UNITS)[number];

export function isMeasurableUnit(unit: string): unit is MeasurableUnit {
  return (MEASURABLE_UNITS as readonly string[]).includes(unit);
}

export function isMassUnit(unit: string): boolean {
  return unit in MASS_IN_UG;
}

export function isVolumeUnit(unit: string): boolean {
  return unit in VOLUME_IN_ML;
}

export function unitsConvertible(from: string, to: string): boolean {
  if (from === to) return true;
  return (isMassUnit(from) && isMassUnit(to)) || (isVolumeUnit(from) && isVolumeUnit(to));
}

/**
 * An amount in another unit: identical units, or within mass, or within volume. Anything
 * else (mg to mL, IU to mg, tablet to mg) is `not_convertible`.
 */
export function convertAmount(
  amount: Ratio,
  fromUnit: string,
  toUnit: string,
): Result<Ratio, 'not_convertible'> {
  if (fromUnit === toUnit) return ok(amount);
  const fromMass = MASS_IN_UG[fromUnit];
  const toMass = MASS_IN_UG[toUnit];
  if (fromMass !== undefined && toMass !== undefined) {
    return ok(multiply(amount, ratio(fromMass, toMass)));
  }
  const fromVolume = VOLUME_IN_ML[fromUnit];
  const toVolume = VOLUME_IN_ML[toUnit];
  if (fromVolume !== undefined && toVolume !== undefined) {
    return ok(multiply(amount, divide(fromVolume, toVolume)));
  }
  return err('not_convertible');
}

// --- Strengths and amounts --------------------------------------------------------------------

export interface Amount {
  readonly value: Ratio;
  readonly unit: string;
}

/** An ingredient's strength: `numerator` per `denominator` ("250 mg per 5 mL", "500 mg per tablet"). */
export interface Strength {
  readonly numerator: Amount;
  readonly denominator: Amount;
}

export type AmountErrorCode = 'not_convertible' | 'ambiguous_ingredient' | 'strength_invalid';

/**
 * How much of one ingredient a single dose contains.
 *
 * - A dose in the product's form or volume unit (2 tablets, 5 mL) is multiplied by the
 *   ingredient's strength.
 * - A dose written in the ingredient's own measure (500 mg) is that ingredient's amount,
 *   but only for a single-ingredient product: for a combination "500 mg" does not say
 *   which ingredient it means, so the answer is `ambiguous_ingredient`, never a guess.
 */
export function ingredientAmountPerDose(
  dose: Amount,
  strength: Strength,
  ingredientCount: number,
): Result<Amount, AmountErrorCode> {
  if (strength.denominator.value.num === 0n) return err('strength_invalid');
  const inDenominator = convertAmount(dose.value, dose.unit, strength.denominator.unit);
  if (inDenominator.ok) {
    const perUnit = divide(inDenominator.value, strength.denominator.value);
    return ok({
      value: multiply(perUnit, strength.numerator.value),
      unit: strength.numerator.unit,
    });
  }
  if (unitsConvertible(dose.unit, strength.numerator.unit)) {
    if (ingredientCount !== 1) return err('ambiguous_ingredient');
    return ok(dose);
  }
  return err('not_convertible');
}

/**
 * A dose expressed in the product's dose unit (the unit a quantity is counted in): the
 * dose itself when already in it (or convertible), or, for a single-ingredient product
 * dosed by the ingredient's measure, the dose divided by the strength.
 */
export function doseInProductUnit(
  dose: Amount,
  productDoseUnit: string,
  strengths: readonly Strength[],
): Result<Amount, AmountErrorCode> {
  const direct = convertAmount(dose.value, dose.unit, productDoseUnit);
  if (direct.ok) return ok({ value: direct.value, unit: productDoseUnit });
  if (strengths.length !== 1)
    return err(strengths.length === 0 ? 'not_convertible' : 'ambiguous_ingredient');
  const [strength] = strengths as [Strength];
  if (strength.numerator.value.num === 0n) return err('strength_invalid');
  const inNumerator = convertAmount(dose.value, dose.unit, strength.numerator.unit);
  if (!inNumerator.ok) return err('not_convertible');
  const denominatorAmount = multiply(
    divide(inNumerator.value, strength.numerator.value),
    strength.denominator.value,
  );
  const inProduct = convertAmount(denominatorAmount, strength.denominator.unit, productDoseUnit);
  if (!inProduct.ok) return err('not_convertible');
  return ok({ value: inProduct.value, unit: productDoseUnit });
}

// --- Frequencies and durations ----------------------------------------------------------------

export const FREQUENCY_KINDS = ['regular', 'as_needed', 'once'] as const;
export type FrequencyKind = (typeof FREQUENCY_KINDS)[number];

/** A configured frequency (plan §2.10). Definitions, not clinical thresholds. */
export interface FrequencyFacts {
  readonly code: string;
  readonly kind: FrequencyKind;
  /** Doses per day × 1000; null for `as_needed` and `once`. */
  readonly dosesPerDayScaled: number | null;
}

export type DosesPerDayErrorCode = 'as_needed_without_maximum' | 'frequency_invalid';

/**
 * How many doses a day this line can give: the frequency's number, or for "as needed" the
 * prescriber's maximum per 24 hours (required, C26). "Once" is one dose in total, which
 * for a daily total is one dose on that day.
 */
export function dosesPerDay(
  frequency: FrequencyFacts,
  maxDosesPerDay: number | null,
): Result<Ratio, DosesPerDayErrorCode> {
  if (frequency.kind === 'once') return ok(ONE);
  if (frequency.kind === 'as_needed') {
    if (maxDosesPerDay === null || !Number.isSafeInteger(maxDosesPerDay) || maxDosesPerDay <= 0) {
      return err('as_needed_without_maximum');
    }
    return ok(ratioFromInteger(maxDosesPerDay));
  }
  if (
    frequency.dosesPerDayScaled === null ||
    !Number.isSafeInteger(frequency.dosesPerDayScaled) ||
    frequency.dosesPerDayScaled <= 0
  ) {
    return err('frequency_invalid');
  }
  return ok(ratioFromScaled(frequency.dosesPerDayScaled, 3));
}

export const DURATION_UNITS = ['days', 'weeks'] as const;
export type DurationUnit = (typeof DURATION_UNITS)[number];

export interface Duration {
  readonly value: number;
  readonly unit: DurationUnit;
}

/** Days in a course. Months are not offered (P7): their length is not a fixed number. */
export function courseDays(duration: Duration): number {
  if (!Number.isSafeInteger(duration.value) || duration.value <= 0) {
    throw new RangeError('A duration is a whole number above zero');
  }
  return duration.unit === 'weeks' ? duration.value * 7 : duration.value;
}

// --- Quantity to dispense ---------------------------------------------------------------------

export interface QuantityProduct {
  /** The unit a clinician doses in by default (tablet, mL). */
  readonly doseUnit: string;
  readonly strengths: readonly Strength[];
  /**
   * How much of the dose unit one stock base unit holds (100 mL per bottle); null when the
   * base unit is the dose unit (one tablet).
   */
  readonly baseUnitContent: Amount | null;
  /** False for creams, inhalers, drops: the prescriber types the quantity. */
  readonly quantityCalculable: boolean;
}

export interface QuantityInput {
  readonly dose: Amount;
  readonly frequency: FrequencyFacts;
  readonly maxDosesPerDay: number | null;
  /** Null only for `once`. */
  readonly duration: Duration | null;
  readonly product: QuantityProduct;
}

export type QuantityCalculationErrorCode =
  'not_calculable' | 'duration_missing' | AmountErrorCode | DosesPerDayErrorCode;

export interface CalculatedQuantity {
  /** Whole base units, rounded up (C10). */
  readonly quantity: number;
  /** The exact course amount in base units, before rounding. */
  readonly exact: Ratio;
}

/**
 * The quantity for the whole course in whole stock base units:
 * dose (in the product's dose unit) × doses per day × days ÷ content per base unit.
 *
 * The course total is rounded UP to whole base units, so a course of 7.5 tablets is 8
 * tablets and a course of 150 mL from 100 mL bottles is 2 bottles.
 * TODO(clinical-verify): rounding the course up (plan §14 C10); doses may be fractions.
 */
export function calculateQuantity(
  input: QuantityInput,
): Result<CalculatedQuantity, QuantityCalculationErrorCode> {
  const { product } = input;
  if (!product.quantityCalculable) return err('not_calculable');
  const dose = doseInProductUnit(input.dose, product.doseUnit, product.strengths);
  if (!dose.ok) return dose;
  const perDay = dosesPerDay(input.frequency, input.maxDosesPerDay);
  if (!perDay.ok) return perDay;
  let total: Ratio;
  if (input.frequency.kind === 'once') {
    total = dose.value.value;
  } else {
    if (input.duration === null) return err('duration_missing');
    total = multiply(
      multiply(dose.value.value, perDay.value),
      ratioFromInteger(courseDays(input.duration)),
    );
  }
  let baseUnits = total;
  if (product.baseUnitContent !== null) {
    const content = convertAmount(
      product.baseUnitContent.value,
      product.baseUnitContent.unit,
      product.doseUnit,
    );
    if (!content.ok || content.value.num === 0n) return err('not_convertible');
    baseUnits = divide(total, content.value);
  }
  const quantity = ceilRatio(baseUnits);
  if (quantity > BigInt(Number.MAX_SAFE_INTEGER)) return err('not_calculable');
  return ok({ quantity: Number(quantity), exact: baseUnits });
}

/**
 * How many whole days a typed quantity covers, for the "Quantity covers 3 of 5 days" note.
 * Null when the per-day amount cannot be worked out.
 */
export function daysCovered(quantity: number, input: QuantityInput): number | null {
  if (input.frequency.kind === 'once' || !input.product.quantityCalculable) return null;
  const dose = doseInProductUnit(input.dose, input.product.doseUnit, input.product.strengths);
  const perDay = dosesPerDay(input.frequency, input.maxDosesPerDay);
  if (!dose.ok || !perDay.ok) return null;
  let perDayBaseUnits = multiply(dose.value.value, perDay.value);
  if (input.product.baseUnitContent !== null) {
    const content = convertAmount(
      input.product.baseUnitContent.value,
      input.product.baseUnitContent.unit,
      input.product.doseUnit,
    );
    if (!content.ok || content.value.num === 0n) return null;
    perDayBaseUnits = divide(perDayBaseUnits, content.value);
  }
  if (perDayBaseUnits.num === 0n) return null;
  return Number(floorRatio(divide(ratioFromInteger(quantity), perDayBaseUnits)));
}
